import apiClient from './api';

// Shared logic for applying a LogTemplate — used by both the TemplatePicker (edit
// then log) and the one-tap template rows on the daily checklist. Applying a
// template EXPANDS its items into HealthEntryCreate rows and POSTs them to the
// existing /api/health/entries write path, stamping data.template_id on each so
// the checklist can honestly tell the routine was logged today.

// Expand a template's items into editable rows. A {ref:"regimen"} item is a live
// placeholder that fans out to the user's current active supplement stack.
export async function expandTemplate(t) {
  const items = t.items || [];
  let stack = [];
  if (items.some((it) => it.ref === 'regimen')) {
    try {
      const res = await apiClient.get('/api/regimen', {
        params: { kind: 'supplement', active_only: true },
      });
      stack = res.data || [];
    } catch {
      stack = [];
    }
  }
  const rows = [];
  for (const it of items) {
    if (it.ref === 'regimen') {
      for (const s of stack) {
        rows.push({
          category_slug: 'nutrition.supplement',
          entry_type: s.name,
          value: s.dose ?? '',
          value_unit: s.dose_unit ?? '',
          data: { supplement_name: s.name, regimen_item_id: s.id, catalog_slug: s.catalog_slug },
          include: true,
        });
      }
    } else {
      rows.push({ ...it, include: true, value: it.value ?? '' });
    }
  }
  return rows;
}

// POST the chosen rows as entries for `date`, stamped with the template id, then
// bump the template's usage. Returns { count, ids } so the caller can offer Undo.
export async function applyTemplateEntries(t, rows, date) {
  const chosen = rows.filter((r) => r.include);
  if (!chosen.length) return { count: 0, ids: [] };
  const payload = chosen.map((r) => ({
    entry_date: date,
    category_slug: r.category_slug,
    entry_type: r.entry_type,
    entry_time: r.entry_time ?? null,
    duration_minutes: r.duration_minutes ?? null,
    value: r.value === '' || r.value == null ? null : Number(r.value),
    value_unit: r.value_unit ?? null,
    secondary_value: r.secondary_value ?? null,
    secondary_unit: r.secondary_unit ?? null,
    calories: r.calories ?? null,
    rating: r.rating ?? null,
    notes: r.notes ?? null,
    // Stamp the template id so the checklist's done-detection is honest and precise.
    data: { ...(r.data || {}), template_id: t.id },
    source: 'template',
  }));
  const res = await apiClient.post('/api/health/entries', payload);
  apiClient.post(`/api/templates/${t.id}/used`).catch(() => {});
  return { count: chosen.length, ids: res.data?.ids || [] };
}

// One-tap: expand with defaults (everything included) and log it straight away.
export async function applyTemplateNow(t, date) {
  const rows = await expandTemplate(t);
  const result = await applyTemplateEntries(t, rows, date);
  return { ...result, name: t.name };
}

// Undo support — delete the entries a template apply just created.
export async function deleteEntries(ids) {
  await Promise.all(
    (ids || []).map((id) => apiClient.delete(`/api/health/entries/${id}`).catch(() => {})),
  );
}
